"use client";

import * as React from "react";

import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "../util.cn";

interface ScrollBarProps
  extends React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > {
  displayScrollBar?: boolean;
}
const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(
  (
    { className, displayScrollBar = true, orientation = "vertical", ...props },
    ref,
  ) => {
    return (
      <ScrollAreaPrimitive.ScrollAreaScrollbar
        className={cn(
          "flex touch-none select-none transition-colors",

          orientation === "vertical"
          && `
            mr-1 h-full w-3 border-l border-l-transparent
            p-px
          `,
          orientation === "horizontal"
          && `
            mb-1 h-3 flex-col border-t border-t-transparent
            p-px
          `,

          className,
        )}
        orientation={orientation}
        ref={ref}
        {...props}
      >
        {displayScrollBar && (
          <ScrollAreaPrimitive.ScrollAreaThumb
            className={`
              relative flex-1 rounded-full bg-gray-8
            `}
          />
        )}
      </ScrollAreaPrimitive.ScrollAreaScrollbar>
    );
  },
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  displayScrollBar?: boolean;
  orientation?: "horizontal" | "vertical";
}
const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    { children, className, displayScrollBar = true, orientation, ...props },
    ref,
  ) => {
    return (
      <ScrollAreaPrimitive.Root
        className={cn("relative overflow-hidden", className)}
        ref={ref}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          className="size-full rounded-[inherit]"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar
          displayScrollBar={displayScrollBar}
          orientation={orientation}
        />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollAreaFlex = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>((props, ref) => {
  const { className, ...scrollAreaProps } = props;

  return (
    <div className="absolute inset-0">
      <ScrollArea
        {...scrollAreaProps}
        className={cn("h-full", className)}
        ref={ref}
      />
    </div>
  );
});
ScrollAreaFlex.displayName = "ScrollAreaFlex";

export { ScrollArea, ScrollAreaFlex, ScrollBar };
